// rolePermissionSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { createRolePermission, deleteRolePermission, fetchRolePermissionsByRoleId, getUserMenuPermissions, updateRolePermission } from './rolePermissionAction';
import { RolePermissionState } from './rolePermissionType';

// Initial State
const initialState: RolePermissionState = {
    rolePermissions: [],
    userMenuPermissions: {
        userId: null,
        roleId: null,
        menuPermissions: {},
    },
    loadingStates: {
        create: false,
        delete: false,
        fetchByRoleId: false,
        update: false,
        fetchUserPermissions: false,
    },
    errors: {
        create: undefined,
        delete: undefined,
        fetchByRoleId: undefined,
        update: undefined,
        fetchUserPermissions: undefined,
    },
};

// Slice
const rolePermissionSlice = createSlice({
    name: 'rolePermissions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Create Role Permission
            .addCase(createRolePermission.pending, (state) => {
                state.loadingStates.create = true;
                state.errors.create = undefined;
            })
            .addCase(createRolePermission.fulfilled, (state, action) => {
                state.loadingStates.create = false;
                state.rolePermissions.push(action.payload);
            })
            .addCase(createRolePermission.rejected, (state, action) => {
                state.loadingStates.create = false;
                state.errors.create = action.payload as string;
            })

            // Delete Role Permission
            .addCase(deleteRolePermission.pending, (state) => {
                state.loadingStates.delete = true;
                state.errors.delete = undefined;
            })
            .addCase(deleteRolePermission.fulfilled, (state, action) => {
                state.loadingStates.delete = false;
                state.rolePermissions = state.rolePermissions.filter(
                    (rolePermission) => rolePermission.RolePermissionId !== action.payload.RolePermissionId
                );
            })
            .addCase(deleteRolePermission.rejected, (state, action) => {
                state.loadingStates.delete = false;
                state.errors.delete = action.payload as string;
            })

            // Fetch Role Permissions by Role ID
            .addCase(fetchRolePermissionsByRoleId.pending, (state) => {
                state.loadingStates.fetchByRoleId = true;
                state.errors.fetchByRoleId = undefined;
            })
            .addCase(fetchRolePermissionsByRoleId.fulfilled, (state, action) => {
                state.loadingStates.fetchByRoleId = false;
                state.rolePermissions = action.payload.data;
            })
            .addCase(fetchRolePermissionsByRoleId.rejected, (state, action) => {
                state.loadingStates.fetchByRoleId = false;
                state.errors.fetchByRoleId = action.payload as string;
            })
            
            // Update Role Permission
            .addCase(updateRolePermission.pending, (state) => {
                state.loadingStates.update = true;
                state.errors.update = undefined;
            })
            .addCase(updateRolePermission.fulfilled, (state, action) => {
                state.loadingStates.update = false;
                // Find and update the role permission in the state
                const index = state.rolePermissions.findIndex(
                    (rolePermission) => rolePermission.RolePermissionId === action.payload.RolePermissionId
                );
                if (index !== -1) {
                    state.rolePermissions[index] = action.payload;
                }
            })
            .addCase(updateRolePermission.rejected, (state, action) => {
                state.loadingStates.update = false;
                state.errors.update = action.payload as string;
            })
            .addCase(getUserMenuPermissions.pending, (state) => {
                state.loadingStates.fetchUserPermissions = true;
                state.errors.fetchUserPermissions = undefined;
            })
            .addCase(getUserMenuPermissions.fulfilled, (state, action) => {
                state.loadingStates.fetchUserPermissions = false;
                state.userMenuPermissions = {
                    userId: action.payload.data.userId,
                    roleId: action.payload.data.roleId,
                    menuPermissions: action.payload.data.menuPermissions,
                };
            })
            .addCase(getUserMenuPermissions.rejected, (state, action) => {
                state.loadingStates.fetchUserPermissions = false;
                state.errors.fetchUserPermissions = action.payload as string;
            });
            
    },
});

export default rolePermissionSlice.reducer;
